/**
 * Roles and the permission matrix.
 *
 * Permissions are plain strings of the form `<resource>:<action>`. The API's
 * `requirePermission()` guard checks membership in ROLE_PERMISSIONS; the web app
 * uses the same map to hide navigation the user cannot use. Keeping one source of
 * truth means a permission added here is immediately enforced and reflected in UI.
 */

export const ROLES = [
  'SUPER_ADMIN',
  'ADMIN',
  'MANAGER',
  'SALESPERSON',
  'GENERAL_TRAINER',
  'PERSONAL_TRAINER',
  'MEMBER',
] as const;

export type Role = (typeof ROLES)[number];

export const STAFF_ROLES: Role[] = [
  'SUPER_ADMIN',
  'ADMIN',
  'MANAGER',
  'SALESPERSON',
  'GENERAL_TRAINER',
  'PERSONAL_TRAINER',
];

export const ROLE_LABELS: Record<Role, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Branch Manager',
  SALESPERSON: 'Salesperson',
  GENERAL_TRAINER: 'General Trainer',
  PERSONAL_TRAINER: 'Personal Trainer',
  MEMBER: 'Member',
};

export const PERMISSIONS = [
  // Org
  'branch:read', 'branch:write',
  'user:read', 'user:write',
  'settings:read', 'settings:write',
  'audit:read',

  // CRM
  'lead:read', 'lead:write', 'lead:assign', 'lead:delete',
  'followup:read', 'followup:write',

  // Members
  'member:read', 'member:write', 'member:delete', 'member:export',

  // Billing
  'plan:read', 'plan:write',
  'subscription:read', 'subscription:write', 'subscription:freeze', 'subscription:cancel',
  'invoice:read', 'invoice:write', 'invoice:cancel',
  'payment:read', 'payment:write', 'payment:refund',
  'discount:approve',

  // Attendance
  'attendance:read', 'attendance:write', 'attendance:manual',
  'slot:read', 'slot:write', 'slot:book',

  // Training
  'pt:read', 'pt:write', 'pt:assign',
  'session:read', 'session:write',
  'workout:read', 'workout:write',
  'diet:read', 'diet:write',
  'health:read', 'health:write',

  // Comms
  'message:read', 'message:send', 'message:template', 'message:campaign', 'whatsapp:admin',

  // Ops
  'feedback:read', 'feedback:write', 'feedback:resolve',
  'inventory:read', 'inventory:write',
  'supplier:read', 'supplier:write',

  // Analytics
  'report:read', 'report:financial',

  // Corporate tie-ups — company accounts, token ledger and monthly billing runs.
  // Separate from billing:* because raising a company invoice for a month of
  // tokens is an account-management job, not a front-desk one.
  'company:read', 'company:write', 'company:bill',

  // People operations
  'roster:read', 'roster:write',
  'leave:read', 'leave:write', 'leave:approve',
  'salary:read', 'salary:write',
  'incentive:read', 'incentive:write', 'incentive:approve',
  'payroll:read', 'payroll:run', 'payroll:approve',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const ALL: Permission[] = [...PERMISSIONS];

const MANAGER_PERMS: Permission[] = [
  'branch:read',
  'user:read',
  'settings:read',
  'lead:read', 'lead:write', 'lead:assign',
  'followup:read', 'followup:write',
  'member:read', 'member:write', 'member:export',
  'plan:read',
  'subscription:read', 'subscription:write', 'subscription:freeze', 'subscription:cancel',
  'invoice:read', 'invoice:write', 'invoice:cancel',
  'payment:read', 'payment:write', 'payment:refund',
  'discount:approve',
  'attendance:read', 'attendance:write', 'attendance:manual',
  'slot:read', 'slot:write', 'slot:book',
  'pt:read', 'pt:write', 'pt:assign',
  'session:read', 'session:write',
  'workout:read', 'diet:read', 'health:read',
  'message:read', 'message:send', 'message:campaign',
  'feedback:read', 'feedback:write', 'feedback:resolve',
  'inventory:read', 'inventory:write',
  'supplier:read', 'supplier:write',
  'report:read', 'report:financial',
  // Runs the floor rota and signs off leave, and can see what the team has
  // earned — but salary structures and payroll sign-off stay with the owner.
  'roster:read', 'roster:write',
  'leave:read', 'leave:write', 'leave:approve',
  'incentive:read', 'incentive:write',
  'payroll:read',
  // A manager runs the tie-up day to day; raising the invoice stays with the owner.
  'company:read', 'company:write',
];

const SALES_PERMS: Permission[] = [
  'lead:read', 'lead:write',
  'followup:read', 'followup:write',
  'member:read', 'member:write',
  'plan:read',
  'subscription:read', 'subscription:write',
  'invoice:read', 'invoice:write',
  'payment:read', 'payment:write',
  'attendance:read',
  'slot:read',
  'pt:read',
  'message:read', 'message:send',
  'feedback:read', 'feedback:write',
  'report:read',
  'roster:read',
  'leave:read', 'leave:write',
  'incentive:read',
  // Needs to see the tie-ups to attach a new joiner to the right employer, but
  // not to change the rate card or raise the company's invoice.
  'company:read',
];

const GENERAL_TRAINER_PERMS: Permission[] = [
  'member:read',
  'attendance:read', 'attendance:write', 'attendance:manual',
  'slot:read', 'slot:write',
  'session:read',
  'workout:read', 'workout:write',
  'diet:read',
  'health:read', 'health:write',
  'feedback:read', 'feedback:write',
  'lead:read', 'lead:write',
  'inventory:read',
  'report:read',
  'roster:read',
  'leave:read', 'leave:write',
  'incentive:read',
];

const PERSONAL_TRAINER_PERMS: Permission[] = [
  'member:read',
  'attendance:read',
  'slot:read',
  'pt:read',
  'session:read', 'session:write',
  'workout:read', 'workout:write',
  'diet:read', 'diet:write',
  'health:read', 'health:write',
  'feedback:read',
  'message:read', 'message:send',
  'report:read',
  'roster:read',
  'leave:read', 'leave:write',
  'incentive:read',
];

/**
 * Members get no staff permissions at all. Every member-facing endpoint is served
 * from `/api/me/*`, which authorises by "is this row mine?" rather than by role,
 * so a member can never reach staff data even if a route is added carelessly.
 */
const MEMBER_PERMS: Permission[] = [];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  SUPER_ADMIN: ALL,
  ADMIN: ALL,
  MANAGER: MANAGER_PERMS,
  SALESPERSON: SALES_PERMS,
  GENERAL_TRAINER: GENERAL_TRAINER_PERMS,
  PERSONAL_TRAINER: PERSONAL_TRAINER_PERMS,
  MEMBER: MEMBER_PERMS,
};

export function roleHasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export function roleHasAny(role: Role, permissions: Permission[]): boolean {
  return permissions.some((p) => roleHasPermission(role, p));
}

/** Roles that may see data across every branch without an explicit branch assignment. */
export const CROSS_BRANCH_ROLES: Role[] = ['SUPER_ADMIN', 'ADMIN'];
